import parse from 'html-react-parser';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import { NextSeo } from 'next-seo';
import SEO from 'next-seo.config';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useRef, useState } from 'react';
import Image, { IMAGE_SIZE } from 'src/2.0/image';
import Layout from 'src/components/layouts';
import { APP_IMG, APP_URL, SHOW_COMMEMORATE } from 'src/constants';
import { IMAGE_DEFAULT, WEB_DESC, WEB_KEYWORD, WEB_NAME, WEB_TITLE, WEB_URL } from 'src/constants/layout';
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
import { KEY_ADS_POSITION } from 'src/utils/ads/dynamic-ads';
import { DATALAYER_TYPE } from 'src/utils/datalayer/send-datalayer';
import { useDesktop } from 'src/utils/device';
import { timestamp } from 'src/utils/helper';
import { GET, GET_ARTICLES } from 'src/utils/services';
import useUser from 'src/utils/useUser';
import styled from 'styled-components';

const PageError = dynamic(import('pages/404'));
const DynamicAds = dynamic(import('src/utils/ads/dynamic-ads'));
const CardV = dynamic(import('src/components/cards/card-v'));
const SectionAffiliate = dynamic(import('src/components/sections/section-event/section-affiliate'));

const PageBylineDetail = ({ articles, nextArticles, category, subCategory, meta, ads, dataAffiliate, dataAdsHeader, dataBylines }) => {
  const _data = useUser();
  const sessionSevId = _data?.user ? decryptionClient(_data?.user) : null;
  if (articles === null) {
    return <PageError dataAdsHeader={dataAdsHeader} sessionSevId={sessionSevId} />;
  }
  const router = useRouter();
  const nextLinkRef = useRef(nextArticles);
  const [nextLink, setNextLink] = useState(nextArticles);
  const listArticlesRef = useRef(articles);
  const [listArticles, setListArticles] = useState(articles);
  const [show, setShow] = useState(24);
  const [adsBox, setAdsBox] = useState(true);
  const [isLoad, setIsLoad] = useState(true);
  const [loadData, setLoadData] = useState(false);
  const handleLightBoxClick = () => {
    setAdsBox(false);
  };
  const handleLoad = async () => {
    setLoadData(true);
    try {
      if (nextLinkRef.current && isLoad) {
        const resData = await GET_ARTICLES(`${nextLinkRef.current || ''}`, false, true);
        if (!isEmpty(resData?.data)) {
          nextLinkRef.current = resData?.nextTo || resData?.links?.next;
          setNextLink(nextLinkRef.current);
          const newLists = [...listArticlesRef.current, ...resData?.data];
          listArticlesRef.current = uniqBy(newLists, 'id');
          setListArticles(listArticlesRef.current);
        }
      }
      setShow(show + 24);
      setIsLoad(!isLoad);
    } catch (err) {
      console.error(`${timestamp()} ==========> LOAD_BYLINES_LIST ERROR : ${nextLinkRef?.current}`, err);
    }
    setLoadData(false);
  };
  const cloneListArticles = [...listArticles];

  const baseUrlBylines = `${APP_URL}/bylines/${encodeURIComponent(dataBylines?.p_name)}`;

  const _article = {
    title: meta?.title || WEB_TITLE,
    description: meta?.description || WEB_DESC,
    keyword: meta?.keyword || WEB_KEYWORD
  };

  const setItemListElement = `[
        {
          "@type": "ListItem",
          "position": 1,
          "name": "หน้าแรก",
          "item": "${APP_URL}"
        },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Bylines",
            "item": "${baseUrlBylines}"
          }
        ]`;

  return (
    <>
      {/* -------------------------------- NEXT_SEO -------------------------------- */}
      <NextSeo
        {...SEO}
        title={_article?.title}
        description={_article?.description}
        openGraph={{
          ...SEO.openGraph,
          title: _article?.title,
          description: _article?.description,
          url: baseUrlBylines
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: _article?.keyword
          }
        ]}
        robotsProps={{
          maxImagePreview: 'large',
          maxSnippet: -1,
          maxVideoPreview: -1
        }}
      />
      {/* --------------------------------- HEADER --------------------------------- */}
      <Head>
        <link rel='canonical' href={baseUrlBylines} />
        <meta property='twitter:title' content={_article?.title} />
        <meta property='twitter:description' content={_article?.description} />
        <meta property='twitter:image' content={IMAGE_DEFAULT} />

        {/* --------------------------------- Truehit -------------------------------- */}
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
                __th_page="${subCategory?.en ? subCategory.en : category?.en}";
            `
          }}
        />
        {/* ----------------------------- ScriptBrowser ------------------------------------ */}
        {!useDesktop() && !isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_mobile}`) : ''}
        {useDesktop() && !isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_desktop}`) : ''}
        {/* ----------------------------- ADS : PPN ------------------------------- */}
        <script async type='text/javascript' src='https://securepubads.g.doubleclick.net/tag/js/gpt.js' />
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: 'var googletag = googletag || {}; window.googletag.cmd = googletag.cmd || [];'
          }}
        />
        {/* ---------------------------- SPIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
              {
                "@context": "https://schema.org",
                "@type": "WebSite",
                "name": "${WEB_NAME}",
                "url": "${WEB_URL.WEB}",
                "description": "${_article?.description}",
                "sameAs": [
                  "${WEB_URL.FACEBOOK}",
                  "${WEB_URL.TWITTER}",
                  "${WEB_URL.INSTAGRAM}",
                  "${WEB_URL.YOUTUBE}",
                  "${WEB_URL.LINE}"
                ],
                "potentialAction": {
                  "@type": "SearchAction",
                  "target": "${WEB_URL.WEB}search?q={search_term_string}",
                  "query-input": "required name=search_term_string"
                }
              }
            `
          }}
        />

        {/* ---------------------------- SPIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                {
                  "@context": "http://schema.org",
                  "@type": "WebPage",
                  "name": "${dataBylines?.p_name}",
                  "description": "${_article?.description}",
                  "publisher": {
                    "@type": "ProfilePage",
                    "name": "${WEB_NAME}"
                  }
                }
              `
          }}
        />

        {/* ------------------------ SPIPPET : BreadcrumbList ------------------------ */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
              {
                "@context": "http://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": ${setItemListElement}
              }
            `
          }}
        />
      </Head>

      {!isEmpty(dataAffiliate) && <SectionAffiliate position={4} data={dataAffiliate} />}
      <Layout sessionSevId={sessionSevId}>
        {/* ---------------------------- ADS : LIGHTBOX ----------------------------- */}
        {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.LIGHTBOX} data={ads} handler={() => handleLightBoxClick()} adsBox={adsBox} />}
        {/* ---------------------------- ADS : BILLBOARD ----------------------------- */}
        {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.BILLBOARD} data={ads} />}

        <SectionWrapper id='section-1' className='category-wrapper'>
          <div className='container'>
            {/* -------------------- Bylines ------------------- */}
            <SectionColumnist style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
              <div className='block-1'>
                <div className='card-item --desktop'>
                  <h1 className='title line-clamp --2 title-h'>{dataBylines?.p_name}</h1>
                  <div
                    className='title line-clamp title-detail'
                    dangerouslySetInnerHTML={{
                      __html: dataBylines?.description
                    }}
                  />
                </div>
                <div className='card-item-image'>
                  <div className='card-image'>
                    <Image src={dataBylines?.image ? '/uploads/images/md/' + dataBylines?.image : '/images/default.jpg'} title={dataBylines?.p_name} size={IMAGE_SIZE.LG} />
                  </div>
                </div>
              </div>
            </SectionColumnist>
            <>
              {listArticles.length < 1 ? (
                <div className='no-article' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
                  <img src={`${APP_IMG}/no-article.png`} alt='no-article' width='100%' />
                </div>
              ) : (
                <div className='content-lists' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
                  {cloneListArticles.slice(0, show).map((item, index) => (
                    <div key={item.id} id={index + 1}>
                      <CardV
                        data={item}
                        description={false}
                        badge={true}
                        path={item.link}
                        //! DATALAYER
                        eventDataLayer={{
                          type: DATALAYER_TYPE.TRACK_POSITION,
                          router: router?.pathname,
                          section: 'ข่าวทั้งหมด',
                          position: 'ใต้:โฆษณาตำแหน่ง billboard',
                          data: {
                            block: '1',
                            title: item.title,
                            heading: dataBylines?.p_name,
                            index: index + 6
                          }
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
              {nextLink && (
                <ButtonContainer style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
                  {loadData ? <text> Loading ... </text> : <button onClick={handleLoad}> ดูเพิ่มเติม</button>}
                </ButtonContainer>
              )}

              <div id='postartice' className='ads-sticky-overflow'>
                {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.POSTARTICLE} data={ads} />}
              </div>
            </>
          </div>
        </SectionWrapper>
        {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.AD1X1} data={ads} />}
      </Layout>
    </>
  );
};

export const getServerSideProps = async ({ query, req, res }) => {
  const initProps = {
    statusCode: 200,
    errMessage: null,
    articles: [],
    meta: {},
    nextArticles: '',
    ads: [],
    dataAffiliate: [],
    tagText: '',
    dataBylines: {},
    dataLatestNews: {}
  };

  try {
    /* --------------------------------- Bylines --------------------------------- */

    // catch-all route: query.id is an array of path segments — join back so names containing "/" survive
    const rawId = Array.isArray(query.id) ? query.id.join('/') : query.id;
    const bylineName = encodeURIComponent(rawId);
    const resData = await GET_ARTICLES(`/api/v1.0/bylines/contents?name=${bylineName}`, false, true);
    const resDataBylines = await GET_ARTICLES('/api/v1.0/bylines?name=' + bylineName, false, true);

    /* --------------------------------- latest-news --------------------------------- */

    const uri = '/api/v1.0/categories/latest-news';
    const latestNews = await GET(uri, false, false);

    if (!isEmpty(resData?.data)) {
      initProps.nextArticles = resData?.nextTo || resData?.links?.next || '';
      initProps.articles = resData.data || [];
      initProps.tagText = rawId || '';
      initProps.meta = {
        ...resData.meta,
        subCategory: { th: rawId, en: rawId },
        category: { th: 'Bylines', en: 'Bylines' }
      };
    }
    if (!isEmpty(resDataBylines?.data)) {
      initProps.dataBylines = resDataBylines.data;
    }
    if (!isEmpty(latestNews)) {
      initProps.dataLatestNews = latestNews;
    }
  } catch (err) {
    console.error(`${timestamp()} ==========> TAGS_LIST ERROR : `, err.message);
    initProps.errMessage = err?.message;
    initProps.statusCode = err?.response?.status || 500;
  }

  return {
    props: initProps
  };
};

export default PageBylineDetail;

const SectionColumnist = styled.div`
  position: relative;
  width: 100%;
  min-width: auto;
  height: auto;
  display: flex;
  flex-direction: column;
  margin-top: 30px;
  .block-1 {
    background-color: #edf4ff;
    flex-direction: row;
    display: flex;
    width: 100%;
    padding: 10px;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: center;
    position: relative;
    justify-content: space-between;
    border-radius: 16px;
    @media (max-width: 425px) {
      padding: 20px;
      justify-content: center;
      align-content: center;
      flex-direction: column-reverse !important;
    }

    .title.line-clamp {
      -webkit-line-clamp: 4;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      white-space: normal;
      text-align: start;
      @media (max-width: 425px) {
        text-align: center;
      }
    }

    .card-item {
      &.--desktop {
        padding-top: 15px;
        max-width: 60%;
        //width: 100%;
        min-width: auto;
        height: auto;
        padding-left: 10px;
        padding-right: 10px;

        @media (max-width: 390px) {
          width: 100%;
        }
        @media (max-width: 597px) {
          width: 100%;
          max-width: 100%;
        }
      }
      &.--mobile {
        width: 100%;
        padding: 8px 0 8px;
        &:nth-child(5) {
          padding: 8px 0 0;
          .card-h .card-content {
            padding: 8px 0 0;
          }
        }
        .card-h {
          border-top: 1px solid #cc9654;
          border-bottom: none;
          .card-content {
            padding: 8px 0 0;
          }
        }
      }
    }
    .card-image {
      position: relative;
      padding-top: 56.25%;
      width: 150px;
      height: 150px;
      justify-content: center;
      align-content: center;
      display: inline-block;

      .resolution-image {
        border-radius: 5px;
        position: absolute;
        width: 100% !important;
        height: 100% !important;
        top: 0;
        left: 0;
      }
    }
    .card-item-image {
      padding-right: 5px;
      padding-top: 5px;
    }
    img {
      padding: 5px;
      border-radius: 50%;
      width: 100%;
      height: auto;
      object-fit: cover;
      object-position: center;
      aspect-ratio: 16/9;
      transform-origin: 50% 65%;
      transition:
        transform 2s,
        filter 0.5s ease-out;
      /* cls */
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;

      border: 2px solid var(--primary-color);
    }
    /* -------------------------------- CONTENTS -------------------------------- */
    .card-content {
      top: 5px;
      @media (max-width: 767px) {
        margin: 10px 0 2.5px;
      }
    }
    /* ---------------------------------- TITLE --------------------------------- */

    .title {
      position: relative;

      font-size: 32px;
      font-weight: 700;
      color: #000;

      &.title-h {
        text-transform: capitalize;
        &:hover {
          color: var(--primary-color);
        }
      }
      &.title-detail {
        font-size: 20px;
        font-weight: unset;
      }
    }

    @media (max-width: 767px) {
      min-height: 35px;
      line-height: 1.35;
      font-size: 16px;
      display: flex;
      flex-direction: row;
    }
    @media (max-width: 597px) {
      min-height: 35px;
      line-height: 1.35;
      font-size: 16px;
      padding: 20px;
      justify-content: center;
      align-content: center;
      flex-direction: column-reverse !important;
    }
  }
`;
const ButtonContainer = styled.div`
  text-align: center;
  padding-bottom: 20px;
  button {
    width: 100%;
    border: 1px solid #e3e3e3;
    background-color: #e3e3e3;
    color: #555555;
    border-radius: 5px;
    font-size: 18px;
    padding: 5px 0;
    :hover {
      border: 1px solid #0600e7;
      background-color: #0600e7;
      color: #fff;
      transition: background-color 0.8ms linear;
    }
  }
`;
const SectionWrapper = styled.div`
  .empty-bg {
    background: unset !important;
  }
  .heading-title {
    font-size: 30pt;
    font-family: 'DBHelvethaicaXBd', sans-serif;
  }
  .content-lists {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    margin-top: 25px;
    @media (max-width: 690px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .block-content {
    .card-v-content-title {
      font-size: 24pt !important;
      font-family: 'DBHelvethaicaXBd', sans-serif !important;
    }
    @media (max-width: 690px) {
      .card-v-content-title {
        font-size: 20pt !important;
      }
      .card-h-content-title {
        font-size: 16pt !important;
      }
    }
  }
`;
